import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { apiErr } from "../lib/api";
import { TASK_STATUS_STYLES } from "../lib/constants";
import { toast } from "sonner";
import { AlertOctagon } from "lucide-react";

const TASK_STATUSES = ["Not Started", "In Progress", "Blocked", "Ready for Review", "Completed"];

function MyRequestCard({ a, onDone }) {
  const rejected = a.status === "rejected";
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ status: "", phase: "", note: "" });
  const resubmit = async () => {
    const payload = {};
    if (form.status) payload.status = form.status;
    if (form.phase) payload.phase = form.phase;
    if (Object.keys(payload).length === 0) return toast.error("Choose a status or phase");
    if (form.note.trim().length < 10) return toast.error("Add a note (min 10 chars)");
    try { await api.post(`/approvals/${a.id}/resubmit`, { payload, submitter_note: form.note }); toast.success("Resubmitted to the PMO"); setEditing(false); onDone(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div data-testid={`my-request-${a.id}`} className="rounded-2xl border border-[#DCE2EA] p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-[#1F2733]">{a.change_summary}</div>
          <div className="text-[11px] text-[#98A2AF] mt-0.5">Submitted {new Date(a.created_at).toLocaleString()}</div>
          {rejected && a.comments && <div className="mt-2 text-xs text-[#8A0822] bg-[#FDF2F4] border border-[#F8C4CC] rounded-lg px-3 py-2"><b>PMO feedback:</b> {a.comments}</div>}
        </div>
        <span className={`text-xs font-semibold px-3 py-1 rounded-full h-fit ${a.status === "approved" ? "bg-[#ECF8F1] text-[#1A5633]" : a.status === "rejected" ? "bg-[#FDF2F4] text-[#8A0822]" : "bg-[#FFF7E6] text-[#824D0E]"}`}>{a.status}</span>
      </div>
      {rejected && !editing && (
        <button data-testid={`resubmit-open-${a.id}`} onClick={() => setEditing(true)} className="mt-3 text-xs font-semibold text-[#BA0C2F] hover:underline">Edit &amp; resubmit</button>
      )}
      {rejected && editing && (
        <div className="mt-3 space-y-2 border-t border-[#DCE2EA] pt-3">
          <div className="grid sm:grid-cols-2 gap-2">
            <select data-testid={`resubmit-status-${a.id}`} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="px-3 py-2 rounded-xl border border-[#DCE2EA] text-sm bg-white">
              <option value="">Status (unchanged)</option>
              {["On Track", "At Risk", "Critical", "Not Started"].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <input data-testid={`resubmit-phase-${a.id}`} value={form.phase} onChange={(e) => setForm({ ...form, phase: e.target.value })} placeholder="Phase (unchanged)" className="px-3 py-2 rounded-xl border border-[#DCE2EA] text-sm" />
          </div>
          <textarea data-testid={`resubmit-note-${a.id}`} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} placeholder="What changed since the rejection? (required, min 10 chars)" className="w-full px-3 py-2 rounded-xl border border-[#DCE2EA] text-sm" />
          <div className="flex gap-2">
            <button data-testid={`resubmit-submit-${a.id}`} onClick={resubmit} className="bg-[#BA0C2F] hover:bg-[#8A0822] text-white rounded-full px-4 py-2 text-sm font-semibold">Resubmit</button>
            <button onClick={() => setEditing(false)} className="text-sm text-[#5A687C] px-3">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function MyResponsibility() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [ws, setWs] = useState([]);
  const [approvals, setApprovals] = useState([]);

  const load = () => {
    api.get(`/tasks?assignee_id=${user.id}`).then((r) => setTasks(r.data)).catch(() => {});
    api.get("/workstreams").then((r) => setWs(r.data)).catch(() => {});
    if (user.role === "it_executive") api.get("/approvals").then((r) => setApprovals(r.data)).catch(() => {});
  };
  useEffect(load, [user.id]);

  const wsCode = (wid) => ws.find((w) => w.id === wid)?.code || "";
  const setStatus = async (t, status) => {
    let blocked_reason;
    if (status === "Blocked") {
      blocked_reason = prompt("What is blocking this task? (required)");
      if (!blocked_reason) return;
    }
    try {
      await api.patch(`/tasks/${t.id}`, { status, blocked_reason, percent_complete: status === "Completed" ? 100 : t.percent_complete });
      toast.success("Status updated"); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const escalate = async (t) => {
    const blocked_reason = prompt("What is blocking this task? (required)");
    if (!blocked_reason) return;
    try { await api.patch(`/tasks/${t.id}`, { status: "Blocked", blocked_reason }); toast.success("Blocker escalated to PMO"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const groups = TASK_STATUSES.map((s) => ({ status: s, items: tasks.filter((t) => t.status === s) })).filter((g) => g.items.length);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl lg:text-3xl font-extrabold text-[#1F2733]">My Responsibility</h1>
        <p className="text-[#5A687C] mt-1">Your assigned tasks — update status right from your phone.</p>
      </div>

      {user.role === "it_executive" && approvals.length > 0 && (
        <div className="bg-white rounded-[18px] border border-[#DCE2EA] p-6" data-testid="my-change-requests">
          <h2 className="font-bold text-[#1F2733] mb-1">My change requests</h2>
          <p className="text-xs text-[#5A687C] mb-4">Track requests you sent to the PMO. Rejected ones can be edited and resubmitted.</p>
          <div className="space-y-3">
            {approvals.map((a) => <MyRequestCard key={a.id} a={a} onDone={load} />)}
          </div>
        </div>
      )}

      {tasks.length === 0 && (
        <div className="bg-white rounded-[18px] border border-[#DCE2EA] p-10 text-center text-[#5A687C]">
          No tasks are currently assigned to you.
        </div>
      )}

      {groups.map((g) => (
        <div key={g.status}>
          <div className="flex items-center gap-2 mb-2">
            <span className={`text-xs font-bold px-3 py-1 rounded-full border ${TASK_STATUS_STYLES[g.status]}`}>{g.status}</span>
            <span className="text-xs text-[#98A2AF]">{g.items.length}</span>
          </div>
          <div className="space-y-3">
            {g.items.map((t) => (
              <div key={t.id} data-testid={`my-task-${t.id}`} className="bg-white rounded-[18px] border border-[#DCE2EA] p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <button onClick={() => nav(`/workstream/${t.workstream_id}`)} className="text-xs font-bold text-[#BA0C2F] hover:underline">{wsCode(t.workstream_id)}</button>
                    <div className="text-sm font-semibold text-[#1F2733] mt-0.5">{t.title}</div>
                    <div className="text-[11px] text-[#98A2AF] mt-0.5">{t.start_date} → {t.end_date}</div>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {TASK_STATUSES.map((s) => (
                    <button key={s} data-testid={`set-status-${s.toLowerCase().replace(/\s+/g, "-")}-${t.id}`} onClick={() => setStatus(t, s)}
                      className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-all ${t.status === s ? "bg-[#BA0C2F] text-white border-[#BA0C2F]" : "border-[#DCE2EA] text-[#5A687C] hover:border-[#BA0C2F]"}`}>{s}</button>
                  ))}
                  <button onClick={() => escalate(t)} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-[#FDF2F4] text-[#8A0822] border border-[#F8C4CC] inline-flex items-center gap-1 ml-auto"><AlertOctagon className="h-3.5 w-3.5" /> Escalate blocker</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
